import { mergeAttributes, Node, type Extensions } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';

/** Hosts the API's sanitiser keeps an iframe from (apps/api/src/lib/html.ts). */
export const VIDEO_EMBED_HOSTS = [
  'www.youtube.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
] as const;

const LINK_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

/** Hrefs the editor will set: absolute http(s), mailto and tel. Relative paths are refused. */
export function isAllowedLinkUrl(raw: string): boolean {
  const value = raw.trim();
  if (!value) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (!LINK_PROTOCOLS.includes(url.protocol)) return false;
  if (url.protocol === 'http:' || url.protocol === 'https:') return url.hostname.length > 0;
  return url.pathname.length > 0;
}

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^\d+$/;

/**
 * Turns a YouTube or Vimeo page or embed URL into the embed URL the storefront may render, or
 * null for any other host or an unrecognised path.
 */
export function toVideoEmbedUrl(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.toLowerCase().replace(/^m\./, 'www.');
  const segments = url.pathname.split('/').filter(Boolean);

  let youtubeId: string | null = null;
  if (host === 'youtu.be') {
    youtubeId = segments[0] ?? null;
  } else if (
    host === 'www.youtube.com' ||
    host === 'youtube.com' ||
    host === 'www.youtube-nocookie.com' ||
    host === 'youtube-nocookie.com'
  ) {
    if (segments[0] === 'watch') youtubeId = url.searchParams.get('v');
    else if (segments[0] === 'embed' || segments[0] === 'shorts' || segments[0] === 'live') {
      youtubeId = segments[1] ?? null;
    }
  }
  if (youtubeId !== null) {
    return YOUTUBE_ID.test(youtubeId)
      ? `https://www.youtube-nocookie.com/embed/${youtubeId}`
      : null;
  }

  let vimeoId: string | null = null;
  if (host === 'player.vimeo.com' && segments[0] === 'video') vimeoId = segments[1] ?? null;
  else if (host === 'vimeo.com' || host === 'www.vimeo.com') vimeoId = segments.at(-1) ?? null;
  if (vimeoId !== null && VIMEO_ID.test(vimeoId))
    return `https://player.vimeo.com/video/${vimeoId}`;
  return null;
}

function isEmbeddableSrc(src: string | null): boolean {
  if (!src) return false;
  try {
    const url = new URL(src);
    return (
      url.protocol === 'https:' && (VIDEO_EMBED_HOSTS as readonly string[]).includes(url.hostname)
    );
  } catch {
    return false;
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    videoEmbed: {
      setVideoEmbed: (attributes: { src: string }) => ReturnType;
    };
  }
}

/**
 * A bare `<iframe>` from an allowed video host. The official YouTube extension wraps the frame in
 * a `div[data-youtube-video]` and knows nothing of Vimeo, so a small node is simpler.
 */
export const VideoEmbed = Node.create({
  name: 'videoEmbed',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      width: { default: '640' },
      height: { default: '360' },
      title: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'iframe[src]',
        getAttrs: (element) => (isEmbeddableSrc(element.getAttribute('src')) ? null : false),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'iframe',
      mergeAttributes(HTMLAttributes, {
        allow:
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
        allowfullscreen: 'true',
        loading: 'lazy',
      }),
    ];
  },

  // A live iframe swallows clicks, so the editor shows it inert and selectable as a whole.
  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const dom = document.createElement('div');
      dom.className = 'rte-video';
      dom.setAttribute('data-video-embed', '');
      const frame = document.createElement('iframe');
      for (const [key, value] of Object.entries(HTMLAttributes)) {
        if (value !== null && value !== undefined) frame.setAttribute(key, String(value));
      }
      frame.setAttribute('src', String(node.attrs.src ?? ''));
      frame.setAttribute('tabindex', '-1');
      frame.style.pointerEvents = 'none';
      dom.append(frame);
      return { dom };
    };
  },

  addCommands() {
    return {
      setVideoEmbed:
        (attributes) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: attributes }),
    };
  },
});

// Tiptap's table writes a <colgroup> and an inline min-width the storefront sanitiser drops;
// emit the plain table instead. Editing still uses the extension's own TableView.
const PlainTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    return ['table', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), ['tbody', 0]];
  },
});

/** The schema of the HTML editor. Every node and mark renders within the API's allowlist. */
export function richTextExtensions(): Extensions {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4, 5, 6] },
      link: {
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: { target: null, rel: null, class: null },
        // Autolinked text may have no scheme yet ("www.example.com"); anything with one must be ours.
        isAllowedUri: (url, { defaultValidate }) =>
          defaultValidate(url) && (!/^[a-z][a-z\d+.-]*:/i.test(url) || isAllowedLinkUrl(url)),
      },
    }),
    TextStyle,
    Color,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'center', 'right', 'justify'],
    }),
    Image.configure({ inline: false, allowBase64: false }),
    PlainTable.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    VideoEmbed,
  ];
}
